import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, CreditCard, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, fmt, track } from "../lib/api";
import { FAQS, NEIGHBORHOODS, ORDER_STEPS, SITE, jsonLd } from "../config/site";

function PageHero({ kicker, lines, copy }) {
  return (
    <div className="bg-brand-forest py-16 text-brand-cream lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-gold">{kicker}</p>
        <MaskedLines lines={lines} className="mt-4 text-4xl font-black leading-[1.0] sm:text-5xl lg:text-6xl" />
        {copy && <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-cream/70">{copy}</p>}
      </div>
    </div>
  );
}

/* ---------------- Delivery ---------------- */

export function DeliveryPage() {
  const regions = ["DC", "MD", "VA"];
  const labels = { DC: "Washington DC", MD: "Maryland", VA: "Northern Virginia" };
  return (
    <div data-testid="delivery-page">
      <SEO title="Delivery Areas" path="/delivery"
        description="CannaCheap DC delivers across Washington DC plus Bethesda, Silver Spring, Takoma Park, Hyattsville, College Park, Arlington and Alexandria. Free delivery $50+." />
      <PageHero
        kicker="Delivery & Pickup"
        lines={["Everywhere", "You Are."]}
        copy={`Same-day delivery across DC and select MD/VA neighborhoods — most orders arrive in 30–90 minutes. Free DC delivery on orders ${fmt(SITE.freeDeliveryMinimum)}+ with code DC80. Or choose express pickup at checkout.`}
      />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10">
        <div className="grid gap-6 sm:grid-cols-3">
          {[
            { icon: Clock, title: "30–90 Minutes", text: "Typical delivery window depending on location and order volume." },
            { icon: CreditCard, title: "Easy Payment", text: `We accept ${SITE.payments.join(", ")}.` },
            { icon: ShieldCheck, title: "21+ Verified", text: "A valid government ID is checked at every handoff." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-brand-line bg-white p-8">
                <c.icon className="h-6 w-6 text-brand-pine" />
                <h2 className="mt-4 font-display text-xl font-bold text-brand-forest">{c.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {regions.map((region) => (
          <div key={region} className="mt-16">
            <Reveal>
              <h2 className="flex items-center gap-3 text-3xl font-black text-brand-forest">
                <MapPin className="h-6 w-6 text-brand-golddeep" /> {labels[region]}
              </h2>
            </Reveal>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {NEIGHBORHOODS.filter((n) => n.region === region).map((n, i) => (
                <Reveal key={n.slug} delay={i * 0.04}>
                  <Link
                    to={`/areas/${n.slug}`}
                    data-testid={`delivery-area-${n.slug}`}
                    className="group block h-full rounded-2xl border border-brand-line bg-white p-6 transition-all hover:border-brand-gold hover:shadow-lg hover:shadow-brand-gold/10"
                  >
                    <div className="flex items-baseline justify-between">
                      <h3 className="font-display text-lg font-bold text-brand-forest">{n.name}</h3>
                      <span className="text-xs text-brand-ink/40">{n.zip}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{n.blurb}</p>
                    <span className="mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-brand-pine">
                      Delivery info <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        ))}

        <Reveal className="mt-16">
          <div className="rounded-[2rem] bg-brand-pine p-10 text-center text-brand-cream">
            <h2 className="text-3xl font-black">Ready when you are.</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-brand-cream/70">
              Open daily {SITE.hours.replace("Open daily · ", "")}. Order online in under two minutes.
            </p>
            <Link to="/shop" data-testid="delivery-shop-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest transition-transform hover:scale-[1.04]">
              Shop the Menu <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

/* ---------------- How to Order ---------------- */

export function HowToOrderPage() {
  return (
    <div data-testid="how-to-order-page">
      <SEO title="How to Order" path="/how-to-order"
        description="Order premium cannabis from CannaCheap DC in four simple steps — browse, build your cart, choose delivery or pickup, confirm. 21+." />
      <PageHero
        kicker="Ordering"
        lines={["Four Steps.", "Zero Hassle."]}
        copy="From browsing to your door in as little as 30 minutes. Here's exactly how it works."
      />
      <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:py-24">
        {ORDER_STEPS.map((step, i) => (
          <Reveal key={step.n} delay={i * 0.05}>
            <div className="grid gap-6 border-b border-brand-line py-12 md:grid-cols-12" data-testid={`order-step-${step.n}`}>
              <span className="font-display text-7xl font-black text-brand-gold/40 md:col-span-3 lg:text-8xl">{step.n}</span>
              <div className="md:col-span-9">
                <h2 className="text-2xl font-black text-brand-forest sm:text-3xl">{step.title}</h2>
                <p className="mt-3 max-w-xl text-base leading-relaxed text-brand-ink/65">{step.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal className="mt-12">
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/shop" data-testid="howto-shop-cta" className="inline-flex items-center gap-2 rounded-full bg-brand-pine px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream transition-transform hover:scale-[1.03]">
              Start Your Order <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={SITE.phoneHref} data-testid="howto-call-cta" className="inline-flex items-center gap-2 rounded-full border-2 border-brand-pine px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-pine">
              <Phone className="h-4 w-4" /> {SITE.phone}
            </a>
          </div>
          <p className="mt-6 text-xs text-brand-ink/50">{SITE.ageLine} · Payments: {SITE.payments.join(", ")}</p>
        </Reveal>
      </div>
    </div>
  );
}

/* ---------------- About ---------------- */

export function AboutPage() {
  return (
    <div data-testid="about-page">
      <SEO title="About Us" path="/about"
        description="CannaCheap DC is a local Washington DC cannabis retailer built on one mission: high-quality, carefully sourced products at prices people can actually afford." />
      <PageHero
        kicker="Our Story"
        lines={["Quality You", "Can Afford."]}
        copy="It's not just our tagline — it's the whole business model."
      />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-3xl font-black text-brand-forest">A DC original.</h2>
            <p className="mt-5 text-base leading-relaxed text-brand-ink/65">
              CannaCheap DC is built on a simple mission: provide high-quality, carefully sourced cannabis
              at prices people can trust. We inspect every product on our menu for consistency, safety and
              effectiveness — because "affordable" should never mean "questionable."
            </p>
            <p className="mt-4 text-base leading-relaxed text-brand-ink/65">
              We're locals serving locals, with fast delivery across every DC quadrant and select Maryland
              and Virginia neighborhoods, plus express pickup for when you're on the move.
            </p>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { n: "01", t: "Sourced with care", d: "Every item is vetted for quality before it hits the menu." },
              { n: "02", t: "Priced with honesty", d: "A local price match promise backs every product we sell." },
              { n: "03", t: "Delivered with discretion", d: "Plain, professional, on-time — every single order." },
              { n: "04", t: "Run with respect", d: "Real humans confirm every order and answer every question." },
            ].map((v, i) => (
              <Reveal key={v.n} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-brand-line bg-white p-6">
                  <span className="font-display text-3xl font-black text-brand-gold/50">{v.n}</span>
                  <h3 className="mt-3 font-display text-lg font-bold text-brand-forest">{v.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="mt-16">
          <div className="rounded-[2rem] border border-brand-gold/40 bg-brand-gold/10 p-10">
            <h2 className="text-2xl font-black text-brand-forest">The Price Match Promise</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-brand-ink/70">
              Find the same product cheaper at another local shop and we match the price — simple as that.
              Combined with free delivery on orders {fmt(SITE.freeDeliveryMinimum)}+ and new daily deals,
              it's how we keep quality affordable.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

/* ---------------- FAQ ---------------- */

export function FaqPage() {
  return (
    <div data-testid="faq-page">
      <SEO title="FAQ" path="/faq"
        description="Answers about CannaCheap DC delivery areas, pickup, payment methods, ordering, delivery minimums and 21+ age requirements."
        jsonLd={{
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question", name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }} />
      <PageHero kicker="Support" lines={["Questions,", "Answered."]} copy="Everything you need to know about ordering, delivery, payment and eligibility." />
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <Accordion type="single" collapsible>
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-brand-line">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-display text-lg font-bold text-brand-forest">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-brand-ink/65">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <Reveal className="mt-12">
          <div className="rounded-2xl border border-brand-line bg-white p-8 text-center">
            <h2 className="font-display text-xl font-black text-brand-forest">Still have a question?</h2>
            <p className="mt-2 text-sm text-brand-ink/60">We're around daily, 10 AM – 10 PM.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <a href={SITE.phoneHref} data-testid="faq-call" className="inline-flex items-center gap-2 rounded-full bg-brand-pine px-6 py-3 text-sm font-bold text-brand-cream">
                <Phone className="h-4 w-4" /> {SITE.phone}
              </a>
              <Link to="/contact" data-testid="faq-contact" className="inline-flex items-center gap-2 rounded-full border border-brand-line px-6 py-3 text-sm font-bold text-brand-pine">
                Contact Us
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

/* ---------------- Contact ---------------- */

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.contact(form);
      track("contact_submit");
      toast.success("Message sent — we'll get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send. Try calling us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact" path="/contact" description="Reach CannaCheap DC — call (202) 422-9366, email info@cannacheap.store, or send a message. Open daily 10 AM – 10 PM." />
      <PageHero kicker="Contact" lines={["Talk to", "a Human."]} copy="Questions about an order, the menu, or delivery to your block? We're here daily, 10 AM – 10 PM." />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="space-y-4">
            {[
              { icon: Phone, label: "Call or text", value: SITE.phone, href: SITE.phoneHref, testid: "contact-phone" },
              { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email" },
              { icon: Clock, label: "Hours", value: SITE.hours, testid: "contact-hours" },
            ].map((c) => (
              <Reveal key={c.label}>
                <a
                  href={c.href || undefined}
                  data-testid={c.testid}
                  className={`flex items-center gap-5 rounded-2xl border border-brand-line bg-white p-6 ${c.href ? "transition-all hover:border-brand-gold" : "cursor-default"}`}
                >
                  <span className="rounded-full bg-brand-pine/5 p-3.5"><c.icon className="h-5 w-5 text-brand-pine" /></span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.2em] text-brand-ink/45">{c.label}</span>
                    <span className="block font-display text-lg font-bold text-brand-forest">{c.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal>
              <p className="rounded-xl border border-brand-gold/40 bg-brand-gold/10 px-5 py-4 text-xs leading-relaxed text-brand-golddeep">
                {SITE.ageLine}. We never share your information. See our{" "}
                <Link to="/privacy-policy" className="underline underline-offset-2">Privacy Policy</Link>.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-2xl border border-brand-line bg-white p-8" data-testid="contact-form">
              <h2 className="font-display text-2xl font-black text-brand-forest">Send a message</h2>
              <div className="mt-6 space-y-4">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-brand-ink/50">Name</label>
                  <input id="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name-input"
                    className="w-full rounded-xl border border-brand-line bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-gold" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-brand-ink/50">Email</label>
                  <input id="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email-input"
                    className="w-full rounded-xl border border-brand-line bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-gold" />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-brand-ink/50">Message</label>
                  <textarea id="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message-input"
                    className="w-full rounded-xl border border-brand-line bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-gold" />
                </div>
                <button type="submit" disabled={busy} data-testid="contact-submit-button"
                  className="w-full rounded-full bg-brand-pine py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream transition-transform hover:scale-[1.01] disabled:opacity-50">
                  {busy ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

export { jsonLd };
